import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdmins, addAdmin, getAdminAssignments } from '../services/api';
import LoadingScreen from '../components/LoadingScreen';

const AdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', phone: '', preferred_language: 'es' });
  const [saving, setSaving] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [adminsData, assignmentsData] = await Promise.all([
        getAdmins(),
        getAdminAssignments(),
      ]);
      setAdmins(adminsData || []);
      setAssignments(assignmentsData || []);
    } catch (err) {
      console.error('Error fetching admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPropertyCount = (adminId) => {
    return assignments.filter(a => a.admin && a.admin.id === adminId).length;
  };

  const handleAddAdmin = async () => {
    if (!newAdmin.name || !newAdmin.phone) return;
    setSaving(true);
    try {
      const result = await addAdmin(newAdmin);
      setShowAddModal(false);
      setNewAdmin({ name: '', phone: '', preferred_language: 'es' });
      const adminId = result?.data?.id || result?.id;
      if (adminId) {
        navigate(`/admins/${adminId}/manage`);
      } else {
        fetchData();
      }
    } catch (err) {
      console.error('Error creating admin:', err);
      alert('Failed to create admin.');
    } finally {
      setSaving(false);
    }
  };

  const filteredAdmins = admins.filter(a =>
    (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (a.phone || '').includes(searchTerm)
  );

  if (loading) return <LoadingScreen message="Loading admins..." />;

  return (
    <div className="ayni-page">
      {/* Header */}
      <div className="ayni-head">
        <h1 className="ayni-display-lg" style={{ margin: 0 }}>Admins</h1>
        <p className="ayni-statline" style={{ marginTop: 8 }}>
          <strong>{admins.length}</strong>{admins.length === 1 ? 'admin' : 'admins'}
        </p>
      </div>

      {/* Toolbar */}
      <div className="ayni-toolbar">
        <div className="ayni-toolbar-group" />
        <div className="ayni-toolbar-group">
          <input
            className="ayni-search"
            type="text"
            placeholder="Search admins..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="ayni-btn primary" onClick={() => setShowAddModal(true)}>
            + Add Admin
          </button>
        </div>
      </div>

      {/* Admins list */}
      {filteredAdmins.length === 0 ? (
        <div className="ayni-empty">No admins found. Click "+ Add Admin" to create one.</div>
      ) : (
        <div className="ayni-card">
          <table className="ayni-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Language</th>
                <th>Properties</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.map((admin) => (
                <tr key={admin.id} onClick={() => navigate(`/admins/${admin.id}/manage`)}>
                  <td><div className="ayni-rowname">{admin.name}</div></td>
                  <td className="ayni-meta">{admin.phone || '—'}</td>
                  <td>
                    <span className={`ayni-badge ${admin.preferred_language === 'es' ? 'amber' : 'blue'}`}>
                      {admin.preferred_language === 'es' ? 'ES' : 'EN'}
                    </span>
                  </td>
                  <td className="ayni-meta">{getPropertyCount(admin.id)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                    <i className="fa-solid fa-chevron-right" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="ayni-modal" onClick={() => setShowAddModal(false)}>
          <div className="ayni-modal-panel" onClick={(e) => e.stopPropagation()}>
            <h2 className="ayni-modal-title">Add New Admin</h2>

            <div className="ayni-field">
              <label className="ayni-label">Name *</label>
              <input
                className="ayni-input"
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                placeholder="Enter name"
              />
            </div>

            <div className="ayni-field">
              <label className="ayni-label">Phone *</label>
              <input
                className="ayni-input"
                value={newAdmin.phone}
                onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </div>

            <div className="ayni-field">
              <label className="ayni-label">Language</label>
              <select
                className="ayni-select"
                value={newAdmin.preferred_language}
                onChange={(e) => setNewAdmin({ ...newAdmin, preferred_language: e.target.value })}
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </div>

            <div className="ayni-modal-actions">
              <button
                className="ayni-btn primary"
                onClick={handleAddAdmin}
                disabled={saving || !newAdmin.name || !newAdmin.phone}
              >
                {saving ? 'Creating...' : 'Create'}
              </button>
              <button
                className="ayni-btn ghost"
                onClick={() => { setShowAddModal(false); setNewAdmin({ name: '', phone: '', preferred_language: 'es' }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminsPage;
